import React, { createContext, ReactNode, useContext, useState } from 'react';
import { decryptPayload, encryptPayload } from '../interceptor/crypto';
import { LoginDto, UsuarioDto, UsuarioResponseDto } from '../interfaces/user';
import UsersService from '../services/UsersService';

interface AuthContextType {
    isAuthenticated: boolean;
    user: UsuarioDto | null;
    login: (email: string, password: string) => Promise<{ success: boolean; message?: string, statusCode: number }>;
    logout: () => void;
    updateUser: (updates: Partial<UsuarioDto>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const AUTH_STATUS_KEY = 'ciclii:isAuthenticated';
const USER_DATA_KEY = 'ciclii:user';

const getDecryptedItem = <T,>(key: string, fallback: T): T => {
    const encrypted = sessionStorage.getItem(key);
    if (!encrypted) {
        return fallback;
    }

    try {
        return decryptPayload<T>(encrypted);
    } catch (error) {
        console.error(`Error al desencriptar la clave ${key}:`, error);
        sessionStorage.removeItem(key);
        return fallback;
    }
};

const setEncryptedItem = (key: string, value: unknown) => {
    try {
        const encrypted = encryptPayload(value);
        sessionStorage.setItem(key, encrypted);
    } catch (error) {
        console.error(`Error al encriptar la clave ${key}:`, error);
        sessionStorage.removeItem(key);
    }
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => {
        const stored = getDecryptedItem<boolean | null>(AUTH_STATUS_KEY, null);
        return stored === true;
    });

    const [user, setUser] = useState<UsuarioDto | null>(() => getDecryptedItem<UsuarioDto | null>(USER_DATA_KEY, null));

    const login = async (email: string, password: string): Promise<{ success: boolean; message?: string, statusCode: number }> => {
        try {
            const loginData: LoginDto = { email, password };
            const response: UsuarioResponseDto = await UsersService.login(loginData);

            if (response && response.statusCode === 200) {
                const nextUser = response.usuario ?? null;
                setUser(nextUser);
                setIsAuthenticated(true);
                setEncryptedItem(AUTH_STATUS_KEY, true);
                setEncryptedItem(USER_DATA_KEY, nextUser);
                return { success: true, message: response.message, statusCode: response.statusCode };
            } else {
                setUser(null);
                setIsAuthenticated(false);
                sessionStorage.clear();
                return { success: false, message: response.message, statusCode: response.statusCode };
            }
        } catch (error: any) {
            setUser(null);
            setIsAuthenticated(false);
            sessionStorage.clear();
            console.error('Error en login:', error);
            return { success: false, message: 'Error en la conexión. Intenta nuevamente.', statusCode: 500 };
        }
    };

    const logout = () => {
        setUser(null);
        setIsAuthenticated(false);
        sessionStorage.clear();
    };

    const updateUser = (updates: Partial<UsuarioDto>) => {
        setUser((prev) => {
            if (!prev) {
                return prev;
            }

            const nextUser: UsuarioDto = { ...prev, ...updates };
            setEncryptedItem(USER_DATA_KEY, nextUser);
            return nextUser;
        });
    };

    return (
        <AuthContext.Provider value={{ isAuthenticated, user, login, logout, updateUser }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth debe ser usado dentro de un AuthProvider');
    }
    return context;
};
